import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Smartphone } from "lucide-react";

export function useResendTimer() {
  const [left, setLeft] = useState(0);
  useEffect(() => { if (left <= 0) return; const t = setTimeout(() => setLeft(left - 1), 1000); return () => clearTimeout(t); }, [left]);
  return [left, setLeft];
}

export function EmailCodeVerify({ mfaToken, email, onComplete, onBack, onUseApp }) {
  const [code, setCode] = useState("");
  const [trust, setTrust] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [left, setLeft] = useResendTimer();
  const started = useRef(false);
  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/email-code/send", { mfa_token: mfaToken }); setSent(true); setLeft(data.resend_in); toast.success(`Code sent to ${data.email}`); }
    catch (err) { toast.error(formatApiError(err)); setSent(true); setLeft(45); } finally { setBusy(false); }
  };
  useEffect(() => { if (!started.current) { started.current = true; send(); } }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/auth/email-code/verify", { mfa_token: mfaToken, code, trust_device: trust }); onComplete(data.user); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="email-code-verify">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Mail className="h-5 w-5 text-blue-700" />Check your email</div>
      <p className="text-xs text-slate-600">{sent ? <>We sent a 6-digit code to <b>{email}</b>. It expires in 10 minutes. Check your spam folder if it doesn't arrive.</> : "Sending your code…"}</p>
      <div><Label htmlFor="email-code">6-digit code</Label><Input id="email-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="font-mono text-lg tracking-widest" autoFocus data-testid="email-code-input" /></div>
      <label className="flex items-center gap-2 text-xs text-slate-700"><input type="checkbox" checked={trust} onChange={(e) => setTrust(e.target.checked)} data-testid="email-code-trust-checkbox" />Trust this device for 30 days</label>
      <Button type="submit" disabled={busy || code.length !== 6} className="w-full bg-blue-700 hover:bg-blue-800" data-testid="email-code-verify-button">{busy ? "Checking…" : "Verify & sign in"}</Button>
      <div className="flex items-center justify-between text-xs">
        <button type="button" onClick={send} disabled={busy || left > 0} className="text-blue-700 underline disabled:text-slate-400 disabled:no-underline" data-testid="email-code-resend">{left > 0 ? `Resend code in ${left}s` : "Resend code"}</button>
        {onUseApp && <button type="button" onClick={onUseApp} className="text-slate-600 underline flex items-center gap-1" data-testid="email-code-use-app"><Smartphone className="h-3 w-3" />Use authenticator app instead</button>}
      </div>
      {onBack && <button type="button" onClick={onBack} className="text-xs text-slate-500 underline w-full text-center" data-testid="email-code-back">Use a different account</button>}
    </form>
  );
}

export function MethodChooser({ email, onPick }) {
  return (
    <div className="space-y-4" data-testid="mfa-method-chooser">
      <div className="text-sm font-semibold text-slate-900">One more step to protect your account</div>
      <p className="text-xs text-slate-600">Every sign-in for <b>{email}</b> needs a second check. Pick the one that suits you — you can change it later in My profile.</p>
      <button type="button" onClick={() => onPick("email")} className="w-full text-left rounded-lg border-2 border-blue-600 bg-blue-50 p-4 hover:bg-blue-100 transition-colors" data-testid="mfa-choose-email">
        <div className="flex items-center gap-2 font-semibold text-slate-900"><Mail className="h-5 w-5 text-blue-700" />Email me a code <span className="ml-auto text-[10px] font-mono uppercase tracking-wide text-blue-700">Recommended · free</span></div>
        <div className="text-xs text-slate-600 mt-1">We email a 6-digit code each time you sign in from a new device. Nothing to install.</div>
      </button>
      <button type="button" onClick={() => onPick("totp")} className="w-full text-left rounded-lg border border-slate-200 bg-white p-4 hover:bg-slate-50 transition-colors" data-testid="mfa-choose-app">
        <div className="flex items-center gap-2 font-semibold text-slate-900"><Smartphone className="h-5 w-5 text-slate-700" />Use an authenticator app</div>
        <div className="text-xs text-slate-600 mt-1">Google Authenticator, Microsoft Authenticator or similar. Works offline.</div>
      </button>
    </div>
  );
}
